import React, { useState } from "react"
import {
	Grid,
	Typography
} from "@material-ui/core"
import { useHistory, useParams } from "react-router-dom"

import { Notification, PopConfirm } from "@/components"
import Breadcrumb from "@/components/BreadcrumbNew"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ErrorHandlerService from "@/services/ErrorHandler"
import MeetingAnalyzerService, {
	MeetingAnalysis
} from "@/services/MeetingAnalyzerService"

import { formatDateToYYYYMMDD } from "@/utils/time"

import MeetingAnalyzerDetailSkeleton from "@/skeletons/Admin/MeetingAnalyzerDetail"

import useStyles from "@/pages/Admin/MeetingAnalyzer/Detail/styles"
import Header from "@/pages/Admin/MeetingAnalyzer/Detail/Header"
import ScoreCard from "@/pages/Admin/MeetingAnalyzer/Detail/ScoreCard"
import ProfileCard from "@/pages/Admin/MeetingAnalyzer/Detail/ProfileCard"
import TalkTimeCard from "@/pages/Admin/MeetingAnalyzer/Detail/TalkTimeCard"
import ObjectionAndConversionCards from "@/pages/Admin/MeetingAnalyzer/Detail/ObjectionAndConversionCards"
import ImprovementsCard from "@/pages/Admin/MeetingAnalyzer/Detail/ImprovementsCard"
import AchievementAndErrorCards from "@/pages/Admin/MeetingAnalyzer/Detail/AchievementAndErrorCards"
import NextStepsCard from "@/pages/Admin/MeetingAnalyzer/Detail/NextStepsCard"
import DateEditPopover from "@/pages/Admin/MeetingAnalyzer/Detail/DateEditPopover"

const LIST_ROUTE = "/admin/meeting-analyzer"

type DateEditState = {
	meetingDate: Date | null
	anchorElement: HTMLButtonElement | null
}

type DetailRouteParams = {
	meetingAnalysisId: string
}

const MeetingAnalyzerDetail: React.FC = () => {
	const classes = useStyles()
	const history = useHistory()
	const { meetingAnalysisId } = useParams<DetailRouteParams>()
	const { triggerValidation } = useValidation()

	const [analysis, setAnalysis] = useState<MeetingAnalysis | null>(null)
	const [loading, setLoading] = useState(true)
	const [dateEdit, setDateEdit] = useState<DateEditState | null>(null)

	const analysisId = Number(meetingAnalysisId)

	const handleLoadAnalysis = async () => {
		setLoading(true)

		try {
			const loadedAnalysis = await MeetingAnalyzerService.retrieveById(analysisId)

			setAnalysis(loadedAnalysis)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	useDidMount(() => {
		handleLoadAnalysis()
	})

	const handleOpenDateEdit = (anchorElement: HTMLButtonElement) => {
		if (!analysis || !analysis.output) {
			return
		}

		const initialDate = analysis.output.meetingDate ? new Date(`${analysis.output.meetingDate}T00:00:00`) : null

		setDateEdit({
			meetingDate: initialDate,
			anchorElement
		})
	}

	const handleCloseDateEdit = () => {
		setDateEdit(null)
	}

	const handleConfirmDateEdit = async (selectedDate: Date | null) => {
		if (!dateEdit || !selectedDate || !analysis) {
			handleCloseDateEdit()
			return
		}

		const meetingDate = formatDateToYYYYMMDD(selectedDate)

		try {
			await MeetingAnalyzerService.updateDate(analysis.id, { meetingDate })

			setAnalysis(prev => prev ? { ...prev, output: prev.output ? { ...prev.output, meetingDate } : prev.output } : prev)

			Notification.success({ message: "Data da reunião atualizada com sucesso." })
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		handleCloseDateEdit()
	}

	const handleDelete = () => {
		if (!analysis) {
			return
		}

		PopConfirm.open({
			title: "Excluir análise",
			description: "Deseja realmente excluir esta análise? Esta ação não pode ser desfeita.",
			confirmButtonText: "EXCLUIR",
			cancelButtonText: "CANCELAR",
			onConfirm: async () => {
				try {
					await MeetingAnalyzerService.delete(analysis.id)

					Notification.success({ message: "Análise excluída com sucesso." })

					history.push(LIST_ROUTE)
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
				}
			}
		})
	}

	if (loading) {
		return <MeetingAnalyzerDetailSkeleton />
	}

	if (!analysis) {
		return (
			<Grid container className={classes.loadingContainer}>
				<Grid item>
					<Typography>
						Análise não encontrada.
					</Typography>
				</Grid>
			</Grid>
		)
	}

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item xs={12}>
				<Breadcrumb
					data={[
						{ name: "Reuniões analisadas", pathname: "/admin/meeting-analyzer" },
						{ name: "Detalhe da análise", pathname: `/admin/meeting-analyzer/${meetingAnalysisId}` }
					]}
				/>
			</Grid>
			<Grid item xs={12}>
				<Header
					analysis={analysis}
					onDelete={handleDelete}
					onOpenDateEdit={handleOpenDateEdit}
				/>
			</Grid>
			<Grid item xs={12}>
				<ScoreCard analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<ProfileCard analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<TalkTimeCard analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<ObjectionAndConversionCards analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<ImprovementsCard analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<AchievementAndErrorCards analysis={analysis} />
			</Grid>
			<Grid item xs={12}>
				<NextStepsCard analysis={analysis} />
			</Grid>
			<DateEditPopover
				value={dateEdit?.meetingDate ?? null}
				anchorElement={dateEdit?.anchorElement ?? null}
				onClose={handleCloseDateEdit}
				onConfirm={handleConfirmDateEdit}
			/>
		</Grid>
	)
}

export default MeetingAnalyzerDetail
